import { motion } from "framer-motion";
import { Users, CalendarDays, Blocks, GraduationCap, TrendingUp } from "lucide-react";

const features = [
  {
    icon: Users,
    title: "Small Groups",
    desc: "Maximum 8 students per class. Every child gets individual attention, feedback, and support from the instructor.",
    color: "#1677FF",
    bg: "#EAF2FF",
  },
  {
    icon: CalendarDays,
    title: "Weekly Lessons",
    desc: "Regular weekly sessions keep kids engaged and build skills consistently. Flexible scheduling for busy families.",
    color: "#F28C38",
    bg: "#FFF1E6",
  },
  {
    icon: Blocks,
    title: "Project-Based Learning",
    desc: "Every lesson leads to a real outcome — a game, animation, or app. Kids learn by creating, not just memorizing.",
    color: "#00A676",
    bg: "#E7FFF6",
  },
  {
    icon: GraduationCap,
    title: "Expert Teacher Guidance",
    desc: "Experienced instructors guide each student through challenges, encouraging independence while providing support.",
    color: "#9333EA",
    bg: "#F3E8FF",
  },
  {
    icon: TrendingUp,
    title: "Structured Curriculum with Progress",
    desc: "A clear learning path with milestones and goals. Parents receive regular updates on their child's development.",
    color: "#D52B1E",
    bg: "#FFE9E7",
  },
];

export const HowClassesWork = () => {
  return (
    <section data-testid="how-classes-work-section" className="pt-[35px] pb-[35px] md:py-24 playzone-gradient noise-bg">
      <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-16 items-start">
          {/* Left: Heading + intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:sticky lg:top-24"
          >
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#EAF2FF] text-[#1677FF] mb-4">
              Learning Format
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-5">
              How{" "}
              <span className="text-[#1677FF]">Classes</span> Work
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed mb-6">
              We've designed every aspect of our classes to help children learn effectively, stay motivated, and genuinely enjoy the process. Here's what makes our approach different.
            </p>
            <img
              src="https://customer-assets.emergentagent.com/job_learn-code-kids-3/artifacts/v71g153w_23d43ab6-b343-4682-904b-5ae87a4763c1.png"
              alt="Girl learning coding on a laptop"
              className="rounded-2xl w-full object-cover shadow-lg"
              loading="lazy"
            />
          </motion.div>

          {/* Right: Feature cards stacked */}
          <div className="space-y-4">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="flex items-start gap-5 p-5 rounded-2xl bg-white border border-border shadow-[var(--shadow-soft)] card-hover"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: feature.bg }}
                >
                  <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                </div>
                <div>
                  <h3 className="font-bold text-base mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowClassesWork;
